import React from 'react';
import { pickBy, isEmpty } from 'lodash';
import { CardHcd } from '@maiia/model/generated/model/api-patient/api-patient';
import { css, styled } from '@mui/material/styles';
import { Box, Stack } from '@mui/material';
import { CardSection, IconClock, IconPhone } from '@maiia/design-system';
import Text from '../../atoms/Text/Text';
import { useTranslation } from '@/src/i18n';
import { getPhoneNumber } from '../../utils/index';

/* JS styling */

const OpeningHoursItem = styled('li', {
  shouldForwardProp: () => true,
})(
  ({ theme }) =>
    css`
      display: grid;
      grid-template-columns: ${theme.spacing(12)} 1fr;
      grid-template-areas: 'day hour';
    `,
);

const OpeningDay = styled('span', {
  shouldForwardProp: () => true,
})(
  () =>
    css`
      grid-area: day;
    `,
);

const OpeningHours = styled('span', {
  shouldForwardProp: () => true,
})(
  () =>
    css`
      grid-area: hour;
    `,
);

/* Component */
interface Props {
  card: CardHcd;
}

export const hasSchedules = (card: CardHcd) =>
  !isEmpty(
    pickBy(
      card?.publicInformation?.officeInformation?.openingSchedules,
      property => property?.isActive,
    ),
  );

export const hasPractitioner = (card: CardHcd) =>
  (card?.center?.practitioners ?? []).filter(
    prat => prat.isVisibleOnline && !prat.isSecret,
  ).length > 0;

const Schedules = ({ card }: Props) => {
  const { t } = useTranslation();
  const openingSchedules =
    card?.publicInformation?.officeInformation?.openingSchedules || {};
  const phoneNumber = getPhoneNumber(card);

  if (!phoneNumber && !hasSchedules(card)) {
    return null;
  }

  return (
    <CardSection
      icon={<IconClock />}
      title={t('opening_hours_and_contact')}
      data-testid="schedules-contact-section"
    >
      <Box
        id="schedules"
        // Without this, when clicking on the tag "Open" (or "Closed"), the page
        // will natively scroll to put this element at the top of the page.
        // However, there is a navbar, which make this element to be hidden. So we
        // apply this CSS to stop the scroll a little lower.
        sx={{ scrollMarginTop: theme => theme.spacing(8) }}
      >
        <Stack spacing={2}>
          {hasSchedules(card) && (
            <ul>
              {Object.keys(openingSchedules).map(
                dayOfWeek =>
                  openingSchedules[dayOfWeek]?.isActive && (
                    <OpeningHoursItem key={dayOfWeek}>
                      <OpeningDay>{`${t(dayOfWeek.toLowerCase())}`}</OpeningDay>
                      <OpeningHours>
                        {` ${openingSchedules[dayOfWeek]?.schedules
                          ?.sort(
                            (a, b) => Number(a.position) - Number(b.position),
                          )
                          .map(
                            ({ startTime, endTime }) =>
                              ` ${startTime} - ${endTime} `,
                          )
                          .join(' / ')}`}
                      </OpeningHours>
                    </OpeningHoursItem>
                  ),
              )}
            </ul>
          )}

          {phoneNumber && (
            <Text
              variant="paragraph"
              component="p"
              iconComponent={<IconPhone aria-label={t('phone_number')} />}
            >
              {phoneNumber}
            </Text>
          )}
        </Stack>
      </Box>
    </CardSection>
  );
};

export default Schedules;
